import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchUserRoles,
  deleteUserRole,
  getUserCountForRole,
} from "@/api/userRoles";
import { UserRole } from "@/types";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Trash,
  Search,
  ArrowUpDown,
  Tag,
  Shield,
  Plus,
  ShieldAlert,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

// Get color for role badge
const getRoleBadgeColor = (roleId: number): string => {
  // Special colors for specific roles
  if (roleId === 1) {
    // SuperAdmin - Red
    return "bg-red-500/10 text-red-500";
  }
  if (roleId === 5) {
    // Standard User - Blue
    return "bg-blue-500/10 text-blue-500";
  }

  // Define a set of vibrant transparent background and text color pairs
  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];

  return colorPairs[roleId % colorPairs.length];
};

const UserRolesList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

  // Check if current user has admin privileges
  const hasAdminPrivileges = (): boolean => {
    if (!currentUser || !currentUser.roles) return false;

    // Check if the user has any system admin role
    return currentUser.roles.some((role) => role.isSystem === true);
  };

  // Check for admin permission and show dialog if needed
  useEffect(() => {
    if (!hasAdminPrivileges()) {
      setPermissionDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for search and sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof UserRole | "userCount";
    direction: "asc" | "desc";
  }>({
    key: "name",
    direction: "asc",
  });

  // State for delete confirmation
  const [deleteRoleItem, setDeleteRoleItem] = useState<UserRole | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Fetch user roles
  const {
    data: roles = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userRoles"],
    queryFn: fetchUserRoles,
  });

  // Get user count for each role
  const { data: userCounts = {}, isLoading: isLoadingCounts } = useQuery({
    queryKey: ["userCounts"],
    queryFn: async () => {
      const counts: Record<number, number> = {};
      for (const role of roles) {
        counts[role.id] = await getUserCountForRole(role.id);
      }
      return counts;
    },
    enabled: roles.length > 0,
  });

  // Get user count for a role
  const getUserCountForRoleId = (roleId: number): number => {
    return userCounts[roleId] || 0;
  };

  // Handle sorting
  const handleSort = (column: keyof UserRole | "userCount") => {
    setSortConfig((prev) => ({
      key: column,
      direction:
        prev.key === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Filter and sort roles
  const filteredAndSortedRoles = [...roles]
    .filter((role) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        role.name.toLowerCase().includes(query) ||
        (role.description && role.description.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      // Always put system roles at the top
      if (a.isSystem && !b.isSystem) return -1;
      if (!a.isSystem && b.isSystem) return 1;

      // Make SuperAdmin always first, then Admin second
      if (a.id === 1) return -1;
      if (b.id === 1) return 1;
      if (a.id === 2) return -1;
      if (b.id === 2) return 1;

      // Then apply normal sorting
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (sortConfig.key === "userCount") {
        const countA = getUserCountForRoleId(a.id);
        const countB = getUserCountForRoleId(b.id);
        return direction === 1 ? countA - countB : countB - countA;
      }

      if (
        typeof a[sortConfig.key] === "string" &&
        typeof b[sortConfig.key] === "string"
      ) {
        return (
          direction *
          (a[sortConfig.key] as string).localeCompare(
            b[sortConfig.key] as string
          )
        );
      }

      return 0;
    });

  const isPageLoading = isLoading || isLoadingCounts;

  const addUserRoleButton = (
    <Button
      onClick={() => navigate("/admin/system/user-roles/new")}
      className="whitespace-nowrap"
    >
      <Plus className="h-4 w-4 mr-2" />
      Add User Role
    </Button>
  );

  return (
    <AdminLayout title="User Roles" actionButton={addUserRoleButton}>
      <div className="flex justify-between items-center mb-6">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Search and filter roles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search roles..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                <TableHead
                  className="w-[180px] cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortConfig.key === "name" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("userCount")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Users</span>
                    {sortConfig.key === "userCount" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortConfig.direction === "asc"
                            ? "transform rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPageLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    {searchQuery
                      ? "No roles found matching your search"
                      : "No roles found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedRoles.map((role) => (
                  <TableRow key={role.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={cn(
                            "font-medium whitespace-nowrap",
                            getRoleBadgeColor(Number(role.id))
                          )}
                        >
                          {role.name}
                        </Badge>
                        {role.isSystem && (
                          <Badge
                            variant="outline"
                            className="bg-amber-500/10 text-amber-500 text-xs border-amber-200 whitespace-nowrap"
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            System
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {role.description || "No description"}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={role.isActive ? "default" : "secondary"}
                        className={
                          role.isActive
                            ? "bg-green-500/10 text-green-500 hover:bg-green-500/20"
                            : "bg-gray-500/10 text-gray-500 hover:bg-gray-500/20"
                        }
                      >
                        {role.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">
                        {getUserCountForRoleId(Number(role.id))}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            navigate(`/admin/system/user-roles/${role.id}/edit`)
                          }
                          disabled={
                            Number(role.id) === 1 || Number(role.id) === 5
                          }
                          title={
                            Number(role.id) === 1
                              ? "SuperAdmin role cannot be edited"
                              : Number(role.id) === 5
                              ? "Standard User role cannot be edited"
                              : "Edit role"
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeleteRoleItem(role);
                            setConfirmDialogOpen(true);
                          }}
                          disabled={
                            Number(role.id) === 1 || Number(role.id) === 5
                          }
                          title={
                            Number(role.id) === 1
                              ? "SuperAdmin role cannot be deleted"
                              : Number(role.id) === 5
                              ? "Standard User role cannot be deleted"
                              : "Delete role"
                          }
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role{" "}
              <strong>{deleteRoleItem?.name}</strong>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!deleteRoleItem) return;

                try {
                  await deleteUserRole(Number(deleteRoleItem.id));
                  queryClient.invalidateQueries({ queryKey: ["userRoles"] });
                  queryClient.invalidateQueries({ queryKey: ["userCounts"] });
                  toast({
                    title: "Role deleted",
                    description: `${deleteRoleItem.name} has been deleted successfully.`,
                  });
                } catch (error) {
                  console.error("Error deleting role:", error);
                  toast({
                    title: "Error",
                    description:
                      error instanceof Error
                        ? error.message
                        : "An error occurred while deleting the role",
                    variant: "destructive",
                  });
                } finally {
                  setConfirmDialogOpen(false);
                  setDeleteRoleItem(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onOpenChange={(open) => {
          setPermissionDialogOpen(open);
          if (!open) {
            navigate("/admin/dashboard");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Permission Required
            </DialogTitle>
            <DialogDescription>
              You need administrator privileges to manage user roles. This
              action is restricted to system administrators only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setPermissionDialogOpen(false);
                navigate("/admin/dashboard");
              }}
            >
              Return to Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserRolesList;
